import cn from 'clsx'
import { LabFeatureFlags } from 'constants/lab'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { isLabCampaignTagPayload } from 'lib/lab/dynamicPayload'
import { StyledSvgIcon, setSvgIconSize } from 'LuxKit/SvgIcon'
import Caption from 'LuxKit/Typography/Caption'
import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'
import { useSiteTakeoverTheme } from '../hooks/useSiteTakeoverTheme'
import Pane from 'components/Common/Pane'

export const LABEL_HEIGHT = rem(24)

export const LabelElement = styled.span`
  height: ${LABEL_HEIGHT};
  max-width: fit-content;
  padding: 0 ${rem(8)};
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  flex-wrap: nowrap;
  border: 1px solid transparent;
  border-radius: ${props => props.theme.borderRadius.S};
  gap: ${rem(4)};

  > ${StyledSvgIcon} {
    ${setSvgIconSize(16)}
  }

  /* 
  TODO: Add more template variants here
  &.kind-primary {
    
  } */
`

type InheritedCaptionProps = Pick<React.ComponentProps<typeof Caption>, 'format'>
type Kind = 'custom'

interface Props extends React.HTMLAttributes<HTMLSpanElement>, InheritedCaptionProps {
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  campaignTagId?: string;
}

/**
 * This is a base 'abstract' label that is used by all the *implementation labels*
 * This should never be used directly. Talk to your designer to discover the correct
 * label type to use in the event you are unsure.
 */
export const SiteTakeoverCampaignTagLabel = React.forwardRef<HTMLSpanElement, Props>((props, ref) => {
  const {
    startIcon,
    endIcon,
    className,
    format,
    campaignTagId,
    ...rest
  } = props

  const siteTakeoverTheme = useSiteTakeoverTheme()
  const enableOfferCampaignTag = useLabFeatureFlag(LabFeatureFlags.enableOfferCampaignTag)

  if (!enableOfferCampaignTag?.enabled || !isLabCampaignTagPayload(enableOfferCampaignTag.value)) {
    return null
  }

  const payload = enableOfferCampaignTag.value

  if (campaignTagId !== payload.campaignId) {
    return null
  }

  // TODO: Add more kind variants here from the CX templates
  const kind: Kind = 'custom'

  return <Pane paletteType={siteTakeoverTheme?.palette}>
    <LabelElement
      {...rest}
      ref={ref}
      className={cn(
        className,
      `kind-${kind}`,
      )}
      style={{
        background: siteTakeoverTheme?.labelStyle,
      }}
    >
      {startIcon}
      <Caption variant="medium" weight="bold" format={format}>{payload.campaignTag}</Caption>
      {endIcon}
    </LabelElement>
  </Pane>
})
